import React, { useContext, useMemo, useRef, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import {
    autoMapColumns,
    ColumnMap,
    parseGoogleCsv,
    RawRow,
    SocioField,
    validateAndDedupe,
    ValidationResult,
} from '../utils/importSocios';

type Step = 1 | 2 | 3 | 4;

interface Props {
    onClose: () => void;
}

const FIELD_LABELS: Partial<Record<SocioField, string>> = {
    nombre: 'Nombre',
    apellido: 'Apellido',
    telefono: 'Teléfono',
    ignore: 'Ignorar',
};

const ALLOWED_FIELDS: SocioField[] = ['nombre', 'apellido', 'telefono'];

// Traducciones de headers comunes de Google Contacts
const HEADER_DISPLAY_ES: Record<string, string> = {
    'First Name': 'Nombre',
    'Middle Name': 'Segundo nombre',
    'Last Name': 'Apellido',
    'Phonetic First Name': 'Nombre fonético',
    'Phonetic Middle Name': 'Segundo nombre fonético',
    'Phonetic Last Name': 'Apellido fonético',
    'Name Prefix': 'Prefijo',
    'Name Suffix': 'Sufijo',
    'Nickname': 'Apodo',
    'File As': 'Archivar como',
    'Organization Name': 'Organización',
    'Organization Title': 'Cargo',
    'Organization Department': 'Departamento',
    'Birthday': 'Cumpleaños',
    'Notes': 'Notas',
    'Photo': 'Foto',
    'Labels': 'Etiquetas',
    'Phone 1 - Label': 'Tipo de teléfono',
    'Phone 1 - Value': 'Teléfono',
    'E-mail 1 - Value': 'Email',
    'Email 1 - Value': 'Email',
};

const sanitizeMapping = (map: ColumnMap): ColumnMap => {
    const out: ColumnMap = {};
    for (const [h, target] of Object.entries(map)) {
        out[h] = ALLOWED_FIELDS.includes(target) ? target : 'ignore';
    }
    return out;
};

const StepIndicator: React.FC<{ current: Step }> = ({ current }) => {
    const steps = ['Instrucciones', 'Mapear columnas', 'Confirmar', 'Resultado'];
    return (
        <div className="flex items-center justify-between mb-6">
            {steps.map((label, idx) => {
                const n = (idx + 1) as Step;
                const active = current === n;
                const done = current > n;
                return (
                    <React.Fragment key={label}>
                        <div className="flex flex-col items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                                {done ? '✓' : n}
                            </div>
                            <span className={`text-xs mt-1 text-center ${active ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>{label}</span>
                        </div>
                        {idx < steps.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-2 ${done ? 'bg-green-500' : 'bg-gray-200'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const StepInstruccionesDropzone: React.FC<{ onFile: (f: File) => void }> = ({ onFile }) => {
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
    };

    return (
        <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors min-h-[260px] flex flex-col items-center justify-center ${
                dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
        >
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.9..."/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12v9m0 0l-3-3m3 3l3-3"/>
            </svg>
            <p className="text-base text-gray-700 font-medium">Arrastrá tu CSV acá o hacé clic para seleccionar</p>
            <p className="text-xs text-gray-500 mt-1">Archivo .csv exportado de Google Contacts</p>
            <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                }}
                className="hidden"
            />
        </div>
    );
};

const InstruccionesSidebar: React.FC<{ collapsed: boolean; onToggle: () => void }> = ({ collapsed, onToggle }) => {
    if (collapsed) {
        return (
            <button
                onClick={onToggle}
                className="self-stretch lg:w-10 w-full bg-gray-50 border-l border-gray-200 hover:bg-gray-100 transition-colors flex lg:flex-col items-center justify-center gap-2 py-3 lg:py-6 text-gray-600"
                title="Mostrar instrucciones y video tutorial"
                aria-label="Mostrar instrucciones y video tutorial"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-xs font-medium lg:[writing-mode:vertical-rl] lg:rotate-180 lg:tracking-wide">
                    Instrucciones y video
                </span>
            </button>
        );
    }
    return (
        <aside className="self-stretch bg-gray-50 border-l border-gray-200 w-full lg:w-80 lg:flex-shrink-0">
            <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700">Cómo exportar tus contactos</h4>
                <button
                    onClick={onToggle}
                    className="text-gray-500 hover:text-gray-800 p-1"
                    title="Ocultar instrucciones"
                    aria-label="Ocultar instrucciones"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            <div className="p-3 space-y-3">
                <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                    <li>Ingresá a <a href="https://contacts.google.com/" target="_blank" rel="noreferrer" className="text-blue-600 underline">contacts.google.com</a></li>
                    <li>Hacé clic en la <strong>flechita hacia arriba</strong> (Exportar) en el panel izquierdo</li>
                    <li>Elegí el formato <strong>Google CSV</strong> y descargá el archivo</li>
                    <li>Subí el archivo en el área de la izquierda</li>
                </ol>
                {/* TODO: reemplazar con embed del video tutorial cuando esté grabado */}
                <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Video tutorial</p>
                    <div className="relative w-full bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
                        <div className="text-center text-gray-500">
                            <svg className="w-10 h-10 mx-auto mb-1 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                            <p className="text-xs font-medium">Video próximamente</p>
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    );
};

const StepMapeo: React.FC<{
    headers: string[];
    rows: RawRow[];
    mapping: ColumnMap;
    onChangeMapping: (m: ColumnMap) => void;
    onBack: () => void;
    onNext: () => void;
}> = ({ headers, rows, mapping, onChangeMapping, onBack, onNext }) => {
    const usedFields = new Set(Object.values(mapping).filter(v => v !== 'ignore'));
    // Mostrar solo columnas mapeadas a un campo del Socio (nombre/apellido/telefono)
    const visibleHeaders = useMemo(
        () => headers.filter(h => ALLOWED_FIELDS.includes(mapping[h])),
        [headers, mapping]
    );
    const mappedCount = visibleHeaders.length;

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                Detectamos <strong>{rows.length} filas</strong> en tu CSV. Solo se importarán las columnas <strong>Nombre</strong>, <strong>Apellido</strong> y <strong>Teléfono</strong>.
            </div>

            <div className="overflow-x-auto border rounded">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {visibleHeaders.map(h => (
                                <th key={h} className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                                    <div className="text-xs text-gray-500 mb-1">{HEADER_DISPLAY_ES[h] || h}</div>
                                    <select
                                        value={mapping[h]}
                                        onChange={e => onChangeMapping({ ...mapping, [h]: e.target.value as SocioField })}
                                        className="w-full p-1 border rounded text-xs"
                                    >
                                        {(Object.keys(FIELD_LABELS) as SocioField[]).map(f => (
                                            <option key={f} value={f} disabled={f !== 'ignore' && f !== mapping[h] && usedFields.has(f)}>
                                                {FIELD_LABELS[f]}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, 5).map((row, idx) => (
                            <tr key={idx} className="border-b">
                                {visibleHeaders.map(h => (
                                    <td key={h} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate">{row[h] || '—'}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length > 5 && (
                    <p className="text-xs text-gray-500 p-2 bg-gray-50 border-t">Mostrando las primeras 5 de {rows.length} filas.</p>
                )}
            </div>

            <div className="text-xs text-gray-600">
                {mappedCount} columna(s) a importar. Se requiere al menos un nombre/apellido y un teléfono.
            </div>

            <div className="flex justify-between gap-2 pt-2">
                <button onClick={onBack} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">← Atrás</button>
                <button onClick={onNext} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold">Continuar →</button>
            </div>
        </div>
    );
};

const StepConfirmar: React.FC<{
    validation: ValidationResult;
    onBack: () => void;
    onImport: () => void;
    importing: boolean;
}> = ({ validation, onBack, onImport, importing }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-green-700">{validation.toImport.length}</div>
                <div className="text-xs text-green-800 mt-1">Listos para importar</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-yellow-700">{validation.duplicates.length}</div>
                <div className="text-xs text-yellow-800 mt-1">Duplicados (se omitirán)</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-red-700">{validation.invalid.length}</div>
                <div className="text-xs text-red-800 mt-1">Inválidos (se omitirán)</div>
            </div>
        </div>

        {validation.toImport.length > 0 && (
            <details className="border rounded" open>
                <summary className="cursor-pointer px-3 py-2 bg-gray-50 text-sm font-medium">Ver socios a importar ({validation.toImport.length})</summary>
                <div className="max-h-48 overflow-y-auto text-sm divide-y">
                    {validation.toImport.slice(0, 50).map((s, i) => (
                        <div key={i} className="px-3 py-2 flex justify-between">
                            <span>{s.nombre} {s.apellido}</span>
                            <span className="text-gray-500 text-xs">{s.telefono || s.email}</span>
                        </div>
                    ))}
                    {validation.toImport.length > 50 && <div className="px-3 py-2 text-xs text-gray-500">... y {validation.toImport.length - 50} más</div>}
                </div>
            </details>
        )}

        {validation.duplicates.length > 0 && (
            <details className="border rounded">
                <summary className="cursor-pointer px-3 py-2 bg-yellow-50 text-sm font-medium">Ver duplicados ({validation.duplicates.length})</summary>
                <div className="max-h-40 overflow-y-auto text-sm divide-y">
                    {validation.duplicates.slice(0, 50).map((d, i) => (
                        <div key={i} className="px-3 py-2">
                            <div className="font-medium">{d.row.nombre} {d.row.apellido}</div>
                            <div className="text-xs text-gray-500">Ya existe: {d.matchedWith.nombre} {d.matchedWith.apellido}</div>
                        </div>
                    ))}
                </div>
            </details>
        )}

        {validation.invalid.length > 0 && (
            <details className="border rounded">
                <summary className="cursor-pointer px-3 py-2 bg-red-50 text-sm font-medium">Ver inválidos ({validation.invalid.length})</summary>
                <div className="max-h-40 overflow-y-auto text-sm divide-y">
                    {validation.invalid.slice(0, 50).map((d, i) => (
                        <div key={i} className="px-3 py-2 text-xs">
                            <span className="text-red-700">{d.reason}</span>
                            <span className="text-gray-500 ml-2">{Object.values(d.row).filter(Boolean).slice(0, 3).join(' · ')}</span>
                        </div>
                    ))}
                </div>
            </details>
        )}

        <div className="flex justify-between gap-2 pt-2">
            <button onClick={onBack} disabled={importing} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50">← Atrás</button>
            <button
                onClick={onImport}
                disabled={importing || validation.toImport.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold disabled:bg-gray-400"
            >
                {importing ? 'Importando...' : `Importar ${validation.toImport.length} socios`}
            </button>
        </div>
    </div>
);

const StepResultado: React.FC<{
    result: { inserted: number; failed: number; error?: string };
    onClose: () => void;
}> = ({ result, onClose }) => (
    <div className="space-y-4 text-center py-4">
        {result.failed === 0 ? (
            <>
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">¡Importación completa!</h3>
                <p className="text-gray-600">Se importaron <strong>{result.inserted}</strong> socios correctamente.</p>
            </>
        ) : (
            <>
                <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
                    </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Importación parcial</h3>
                <p className="text-gray-600">
                    Se importaron <strong>{result.inserted}</strong> socios. <strong className="text-red-600">{result.failed}</strong> fallaron.
                </p>
                {result.error && <p className="text-xs text-red-600 bg-red-50 rounded p-2 mt-2">{result.error}</p>}
            </>
        )}
        <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold">Cerrar</button>
    </div>
);

const ImportSociosWizard: React.FC<Props> = ({ onClose }) => {
    const { socios, bulkAddSocios } = useContext(AppContext);
    const ui = useUI();
    const [step, setStep] = useState<Step>(1);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<RawRow[]>([]);
    const [mapping, setMapping] = useState<ColumnMap>({});
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState<{ inserted: number; failed: number; error?: string } | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const validation = useMemo(
        () => (step >= 3 ? validateAndDedupe(rows, mapping, socios) : null),
        [step, rows, mapping, socios]
    );

    const reset = () => {
        setStep(1);
        setHeaders([]);
        setRows([]);
        setMapping({});
        setImporting(false);
        setResult(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFile = async (file: File) => {
        if (file.size > 5 * 1024 * 1024) {
            ui.showToast('error', 'El archivo es demasiado grande (máx. 5MB).');
            return;
        }
        try {
            const { headers, rows } = await parseGoogleCsv(file);
            if (headers.length === 0 || rows.length === 0) {
                ui.showToast('error', 'El CSV está vacío o no tiene encabezados.');
                return;
            }
            setHeaders(headers);
            setRows(rows);
            setMapping(sanitizeMapping(autoMapColumns(headers)));
            setStep(2);
        } catch (err: any) {
            ui.showToast('error', 'No se pudo leer el archivo: ' + (err?.message || 'error desconocido'));
        }
    };

    const handleImport = async () => {
        if (!validation || validation.toImport.length === 0) return;
        setImporting(true);
        const res = await bulkAddSocios(validation.toImport);
        setResult(res);
        setImporting(false);
        setStep(4);
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6 border-b flex flex-wrap gap-3 justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Importar socios desde CSV</h2>
                <button
                    onClick={handleClose}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 font-semibold"
                >
                    &larr; Volver al listado
                </button>
            </div>
            <div className="flex flex-col lg:flex-row items-stretch">
                <div className="flex-1 min-w-0 p-4 md:p-6 space-y-6">
                    <StepIndicator current={step} />
                    {step === 1 && <StepInstruccionesDropzone onFile={handleFile} />}
                    {step === 2 && (
                        <StepMapeo
                            headers={headers}
                            rows={rows}
                            mapping={mapping}
                            onChangeMapping={setMapping}
                            onBack={() => setStep(1)}
                            onNext={() => setStep(3)}
                        />
                    )}
                    {step === 3 && validation && (
                        <StepConfirmar
                            validation={validation}
                            onBack={() => setStep(2)}
                            onImport={handleImport}
                            importing={importing}
                        />
                    )}
                    {step === 4 && result && <StepResultado result={result} onClose={handleClose} />}
                </div>
                <InstruccionesSidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />
            </div>
        </div>
    );
};

export default ImportSociosWizard;
